import React from "react";
import {
  Box,
  Button,
  Divider,
  Flex,
  FormControl,
  FormLabel,
  Heading,
  Link,
  Slider,
  SliderFilledTrack,
  SliderThumb,
  SliderTrack,
  Spacer,
  Text,
  useToast,
  VStack,
} from "@chakra-ui/react";
import { FiCheck, FiMail, FiThermometer, FiUser } from "react-icons/fi";
import { RxDiscordLogo } from "react-icons/rx";

import { zAuthenticationStrategy } from "@charry/models";
import { wait } from "@charry/utils";

import AuthUtil from "~/lib/auth";
import { api } from "~/lib/trpc";
import useUser from "~/ui/hooks/useUser.hook";
import SettingsLayout from "~/ui/layouts/Settings.layout";

const DELETE_USER_AVAILABLE = false as boolean;

const AccountSettingsPage: React.FC = () => {
  const { logout, refreshUser, user } = useUser();
  const toast = useToast();

  const [temperature, setTemperature] = React.useState(
    user?.temperature ?? 0.7,
  );

  React.useEffect(() => {
    if (user?.temperature !== undefined) {
      setTemperature(user.temperature);
    }
  }, [user?.temperature]);

  const handleTemperatureChange = (value: number) => {
    setTemperature(value);
  };

  const handleTemperatureChangeEnd = (value: number) => {
    void handleSave(value);
  };

  const tempChangeMutation = api.user.setTemperature.useMutation();
  const deleteUserMutation = api.user.deleteUser.useMutation();

  const handleSave = async (value: number) => {
    if (user) {
      try {
        await tempChangeMutation.mutateAsync({
          ...user,
          temperature: value,
        });
        await refreshUser();
        toast({
          description: "Your temperature setting has been saved successfully.",
          duration: 3000,
          isClosable: true,
          status: "success",
          title: "Temperature Updated",
        });
      } catch (error) {
        console.error("Error updating temperature:", error);
        toast({
          description: "Failed to update temperature. Please try again.",
          duration: 5000,
          isClosable: true,
          status: "error",
          title: "Error",
        });
      }
    }
  };

  const unlinkDiscordMutation = api.user.unlinkDiscordAccount.useMutation();

  const handleLinkDiscord = async () => {
    try {
      const result = await AuthUtil.startOAuthFlow(
        zAuthenticationStrategy.Enum.Discord,
      );

      if (result.error) {
        throw new Error(result.error.message);
      }

      if (result.user) {
        await refreshUser();
        toast({
          description: "Discord account linked successfully.",
          duration: 5000,
          isClosable: true,
          status: "success",
          title: "Success",
        });
      }
    } catch (error) {
      console.error("Error linking Discord account:", error);
      toast({
        description: "Failed to link Discord account. Please try again.",
        duration: 5000,
        isClosable: true,
        status: "error",
        title: "Error",
      });
    }
  };

  const handleUnlinkDiscord = async () => {
    try {
      await unlinkDiscordMutation.mutateAsync();
      await refreshUser();
      toast({
        description: "Discord account unlinked successfully.",
        duration: 5000,
        isClosable: true,
        status: "success",
        title: "Success",
      });
    } catch (error) {
      console.error("Error unlinking Discord account:", error);
      toast({
        description: "Failed to unlink Discord account. Please try again.",
        duration: 5000,
        isClosable: true,
        status: "error",
        title: "Error",
      });
    }
  };

  const handleDeleteAccount = async () => {
    try {
      const isConfirmed = window.confirm(
        "Are you sure you want to delete your account? This action cannot be undone.",
      );
      if (!isConfirmed) {
        return;
      }

      await deleteUserMutation.mutateAsync();
      toast({
        description:
          "Account deleted successfully. You will now be logged out.",
        duration: 5000,
        isClosable: true,
        status: "success",
        title: "Success",
      });
      await wait.timeout(3000);
      logout();
    } catch (err) {
      console.error("Error deleting account:", err);
      toast({
        description: "Failed to delete account. Please try again.",
        duration: 5000,
        isClosable: true,
        status: "error",
        title: "Error",
      });
    }
  };

  return (
    <SettingsLayout
      description="Manage your Charry account settings"
      title="Account Settings"
    >
      <VStack align="stretch" spacing={6}>
        <Divider />
        <Box>
          <Heading as="h2" mb={2} size="md">
            <Flex alignItems="center">
              <FiUser />
              <Text ml={2}>Account Information</Text>
            </Flex>
          </Heading>
          <Text>Name: {user?.firstName ?? "Not set"}</Text>
          <Text>Username: {user?.username ?? "Not set"}</Text>
          <Text>Email: {user?.email}</Text>
        </Box>
        <Divider />
        <FormControl>
          <FormLabel>
            <Flex alignItems="center">
              <FiThermometer />
              <Text ml={2}>Character Temperature Level</Text>
            </Flex>
          </FormLabel>
          <Text color="gray.500" fontSize="sm" mb={2}>
            Adjust how creative or focused your AI characters will be in their
            responses.
          </Text>
          <Flex alignItems="center" mb={2}>
            <Text color="blue.500" fontSize="sm">
              More Focused
            </Text>
            <Spacer />
            <Text color="blue.500" fontSize="sm">
              More Creative
            </Text>
          </Flex>
          <Flex alignItems="center">
            <Slider
              flex="1"
              max={1}
              min={0}
              onChange={handleTemperatureChange}
              onChangeEnd={handleTemperatureChangeEnd}
              step={0.1}
              value={temperature}
            >
              <SliderTrack bg="gray.600">
                <SliderFilledTrack bg="blue.500" />
              </SliderTrack>
              <SliderThumb bg="blue.600" boxSize={6}>
                <Box as={FiThermometer} color="white" />
              </SliderThumb>
            </Slider>
            <Text ml={4} textAlign="right" width="40px">
              {temperature.toFixed(1)}
            </Text>
          </Flex>
          <Text color="gray.500" fontSize="sm" mt={2}>
            Higher values (towards 1.0) make responses more creative and varied.
            Lower values (towards 0.1) make responses more focused and
            deterministic. The default value is 0.7 for a balance between
            creativity and focus.
          </Text>
        </FormControl>
        <Divider />
        <Box>
          <Flex alignItems="center" justifyContent="space-between">
            <Flex alignItems="center">
              <FiMail size={20} />
              <Text fontSize="lg" ml={2}>
                Email Verification
              </Text>
            </Flex>
            {user?.emailVerifiedAt ? (
              <Flex alignItems="center" color="green.500">
                <FiCheck />
                <Text ml={2}>Verified</Text>
              </Flex>
            ) : (
              <Link href={"/verify-email"} target="_blank">
                <Button variant="outline">Verify Email</Button>
              </Link>
            )}
          </Flex>
        </Box>
        <Divider />
        <Box>
          <Flex alignItems="center" justifyContent="space-between">
            <Flex alignItems="center">
              <RxDiscordLogo size={20} />
              <Text fontSize="lg" ml={2}>
                Discord Connection
              </Text>
            </Flex>
            {user?.discordId ? (
              <Flex alignItems="flex-end" flexDirection="column">
                <Flex alignItems="center" color="green.500">
                  <FiCheck />
                  <Text ml={2}>Connected</Text>
                  <Text fontSize="sm" ml={2}>
                    "{user.discordUsername}"
                  </Text>
                </Flex>
                <Button
                  mt={2}
                  onClick={handleUnlinkDiscord}
                  size="sm"
                  variant="outline"
                >
                  Unlink Discord
                </Button>
              </Flex>
            ) : (
              <Button onClick={handleLinkDiscord} variant="outline">
                Link Discord
              </Button>
            )}
          </Flex>
        </Box>
        {DELETE_USER_AVAILABLE && (
          <>
            <Divider />
            <Box>
              <Flex alignItems="center" justifyContent="space-between">
                <Flex alignItems="center">
                  <RxDiscordLogo size={20} />
                  <Text fontSize="lg" ml={2}>
                    Delete Account
                  </Text>
                </Flex>
                <Button onClick={handleDeleteAccount} variant="outline">
                  Delete Account
                </Button>
              </Flex>
            </Box>
          </>
        )}
        <Box height="48px" />
      </VStack>
    </SettingsLayout>
  );
};

export default AccountSettingsPage;
